import React, { useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import DashboardLayout from "layout";
import ConfirmDialog from "components/ConfirmDialog";
import { FieldLabel, fieldSx } from "components/SideDrawer";
import { useToast } from "components/Toast";
import { optInEsimNotifications, simulateEsimWebhook, syncEsimPackages } from "services/esim";
import { getApiError } from "services/finance";
import { isStaging } from "utils/env";
import AiraloBalance from "./AiraloBalance";

// Labels are for display; `value` is the code the backend expects.
const WEBHOOK_EVENTS = [
  { value: "low_data_notification", label: "Low data alert" },
  { value: "credit_limit_notification", label: "Credit limit alert" },
];
const WEBHOOK_TYPES = [
  { value: "data_75", label: "75% of data used" },
  { value: "data_90", label: "90% of data used" },
  { value: "expire_3", label: "Expires in 3 days" },
  { value: "expire_1", label: "Expires in 1 day" },
];

const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <Box bgcolor="#FFFFFF" border="1px solid #E4E7EC" borderRadius="12px" p="20px">
    <Typography fontSize={16} fontWeight={600} color="#101828">
      {title}
    </Typography>
    <Typography fontSize={13} color="#667085" mb="16px">
      {description}
    </Typography>
    {children}
  </Box>
);

const EsimTools = () => {
  const { showToast } = useToast();
  const [webhookUrl, setWebhookUrl] = useState("");
  const [levels, setLevels] = useState("70, 90");
  const [confirmOptIn, setConfirmOptIn] = useState(false);
  const [sim, setSim] = useState({
    event: WEBHOOK_EVENTS[0].value,
    type: WEBHOOK_TYPES[0].value,
    iccid: "",
    webhook_url: "",
  });

  const sync = useMutation({
    mutationFn: syncEsimPackages,
    // 202: the sync runs in the background, so this is "started", not "done".
    onSuccess: (res) => showToast(res?.message || "Package sync started", "info"),
    onError: (error) => showToast(getApiError(error, "Failed to start sync"), "error"),
  });

  const parsedLevels = levels
    .split(",")
    .map((l) => Number(l.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);

  const optIn = useMutation({
    mutationFn: () =>
      optInEsimNotifications({ webhook_url: webhookUrl.trim(), credit_limit_levels: parsedLevels }),
    onSuccess: (res) => {
      showToast(res?.message || "Subscribed to Airalo notifications", "success");
      setConfirmOptIn(false);
    },
    onError: (error) => {
      showToast(getApiError(error, "Failed to subscribe"), "error");
      setConfirmOptIn(false);
    },
  });

  const simulate = useMutation({
    mutationFn: () => simulateEsimWebhook({ ...sim, iccid: sim.iccid.trim(), webhook_url: sim.webhook_url.trim() }),
    onSuccess: (res) => showToast(res?.message || "Test event requested", "info"),
    onError: (error) => showToast(getApiError(error, "Failed to simulate webhook"), "error"),
  });

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ width: "100%", p: { xs: 0, sm: 2 } }}>
        <Box mb={3}>
          <Typography variant="h5" fontWeight={600} color="#101828">
            eSIM Tools
          </Typography>
          <Typography variant="body2" color="#475467">
            Airalo account and catalog tools
          </Typography>
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap="16px">
          <Card
            title="Airalo balance"
            description="Prepaid credit used to buy eSIMs. Orders fail when it runs low."
          >
            <AiraloBalance />
          </Card>

          <Card
            title="Sync packages"
            description="Refreshes the plan catalog from Airalo. Runs in the background and also runs hourly."
          >
            <Button
              variant="contained"
              disabled={sync.isPending}
              onClick={() => sync.mutate()}
              sx={{ textTransform: "none" }}
            >
              {sync.isPending ? "Starting..." : "Sync packages"}
            </Button>
          </Card>

          <Card
            title="Webhook opt-in"
            description="One-time setup: subscribes our webhook to Airalo's low-data and credit-limit alerts."
          >
            <Box display="flex" flexDirection="column" gap="12px">
              <Box>
                <FieldLabel>Webhook URL</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  placeholder="https://…/api/v1/esim/webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
              </Box>
              <Box>
                <FieldLabel>Credit limit levels (%)</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  value={levels}
                  onChange={(e) => setLevels(e.target.value)}
                  helperText="Comma-separated, e.g. 70, 90"
                />
              </Box>
              <Box>
                <Button
                  variant="outlined"
                  disabled={!webhookUrl.trim() || !parsedLevels.length}
                  onClick={() => setConfirmOptIn(true)}
                  sx={{ textTransform: "none" }}
                >
                  Subscribe
                </Button>
              </Box>
            </Box>
          </Card>

          {isStaging && (
            <Card
              title="Simulate webhook (sandbox)"
              description="Sandbox doesn't deliver webhooks; this asks Airalo to send a test event."
            >
              <Box display="flex" flexDirection="column" gap="12px">
                <Box display="flex" gap="12px">
                  <TextField
                    select
                    size="small"
                    label="Event"
                    fullWidth
                    value={sim.event}
                    onChange={(e) => setSim({ ...sim, event: e.target.value })}
                  >
                    {WEBHOOK_EVENTS.map((ev) => (
                      <MenuItem key={ev.value} value={ev.value}>
                        {ev.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Type"
                    fullWidth
                    value={sim.type}
                    onChange={(e) => setSim({ ...sim, type: e.target.value })}
                  >
                    {WEBHOOK_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <TextField
                  size="small"
                  label="ICCID"
                  value={sim.iccid}
                  onChange={(e) => setSim({ ...sim, iccid: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Webhook URL"
                  value={sim.webhook_url}
                  onChange={(e) => setSim({ ...sim, webhook_url: e.target.value })}
                />
                <Box>
                  <Button
                    variant="outlined"
                    disabled={!sim.iccid.trim() || !sim.webhook_url.trim() || simulate.isPending}
                    onClick={() => simulate.mutate()}
                    sx={{ textTransform: "none" }}
                  >
                    {simulate.isPending ? "Sending..." : "Send test event"}
                  </Button>
                </Box>
              </Box>
            </Card>
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={confirmOptIn}
        title="Subscribe webhook?"
        message={`Airalo will send low-data and credit-limit alerts to ${webhookUrl.trim()}. This is normally done once per environment.`}
        confirmLabel="Subscribe"
        loading={optIn.isPending}
        onConfirm={() => optIn.mutate()}
        onClose={() => setConfirmOptIn(false)}
      />
    </DashboardLayout>
  );
};

export default EsimTools;
